import { resolveNavigationRule, type NavigationScreen } from "@/platform/app-mode/navigation-rules"
import type { VisitAction, VisitDecision } from "@/platform/bridge/types"

export const VISIT_DECISION_TIMEOUT_MS = 400

export type VisitOutcome = VisitDecision | "timeout"

export interface AnchorLike {
  readonly href: string
  readonly target: string
  readonly download: string
  readonly rel: string
}

export interface ClickLike {
  readonly button: number
  readonly metaKey: boolean
  readonly ctrlKey: boolean
  readonly shiftKey: boolean
  readonly altKey: boolean
}

const ROUTABLE_PROTOCOLS: ReadonlySet<string> = new Set(["http:", "https:"])

const SELF_TARGETS: ReadonlySet<string> = new Set(["", "_self"])

const parse = (url: string, base?: string): URL | null => {
  try {
    return new URL(url, base)
  } catch {
    return null
  }
}

export const interceptableUrl = (anchor: AnchorLike, currentHref: string): string | null => {
  if (anchor.download !== "" || !SELF_TARGETS.has(anchor.target) || anchor.rel.split(/\s+/).includes("external")) return null

  const next = parse(anchor.href, currentHref)
  const current = parse(currentHref)
  if (next === null || current === null || !ROUTABLE_PROTOCOLS.has(next.protocol) || next.origin !== current.origin) return null

  return `${next.pathname}${next.search}` === `${current.pathname}${current.search}` ? null : next.href
}

export const isModifiedClick = (event: ClickLike): boolean => event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

export const visitActionFor = (currentHref: string, nextHref: string): VisitAction => {
  const current = parse(currentHref)
  const next = parse(nextHref)

  return current !== null && next !== null && current.pathname === next.pathname ? "replace" : "advance"
}

const localScreen = (url: string): NavigationScreen | undefined => {
  const parsed = parse(url)
  const candidate = parsed === null ? url : `${parsed.pathname}${parsed.search}`

  return resolveNavigationRule(candidate)?.screen
}

export const hintable = (url: string): boolean => {
  const parsed = parse(url)
  const candidate = parsed === null ? url : parsed.pathname
  const rule = resolveNavigationRule(candidate)
  return rule?.screen === "push" && rule.prefetch === true
}

export const proceedsInPage = (outcome: VisitOutcome, url: string): boolean => outcome === "in-page" || (outcome === "timeout" && localScreen(url) === "in-page")
